import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Card } from '../Card/Card';

export type TranslateFn = (
  key: string,
  defaultValue: string,
  options?: Record<string, unknown>,
) => string;

interface ContestInfoCardProps {
  contest: any;
  onClick: () => void;
  applicationState?: string;
  myEntries?: number;
  t?: TranslateFn;
  now?: Date;
}

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const REGISTRATION_WINDOW_DAYS = 30;

const safeNumber = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value)
    ? value
    : Number(value) || 0;

const toTime = (iso?: string) => {
  if (!iso) return null;
  const value = Date.parse(iso);
  return Number.isNaN(value) ? null : value;
};

const daysUntil = (dateIso?: string, now = new Date()) => {
  const target = toTime(dateIso);
  if (!target) return null;

  return Math.ceil((target - now.getTime()) / DAY_IN_MS);
};

const deadlineProgress = (
  endsAt?: string,
  now = new Date(),
  startsAt?: string,
) => {
  const end = toTime(endsAt);
  if (!end) return 0;

  const start = toTime(startsAt);
  const nowMs = now.getTime();

  if (start && start < end) {
    const total = end - start;
    if (total <= 0) return 0;
    return Math.max(0, Math.min(1, (nowMs - start) / total));
  }

  const approxStart = end - REGISTRATION_WINDOW_DAYS * DAY_IN_MS;
  const total = end - approxStart;
  return Math.max(0, Math.min(1, (nowMs - approxStart) / total));
};

const getCountdownInfo = (edition: any, now: Date, t: TranslateFn) => {
  const state = edition?.state;
  const calendar = edition?.calendar;

  const getMessage = (
    key: string,
    defaultValue: string,
    targetDate?: string,
  ) => {
    const remainingDays = daysUntil(targetDate, now);
    if (remainingDays === null) return null;

    return remainingDays >= 0
      ? t(key, defaultValue, { days: remainingDays })
      : t(
          'Pages:ParticipantWeb.ContestsPage.deadlinePassed',
          'Deadline passed',
        );
  };

  if (state === 'published') {
    const message = getMessage(
      'Pages:ParticipantWeb.ContestsPage.registrationStartsInDays',
      'Registration starts in {{days}} days',
      calendar?.registrationStartsAt,
    );
    if (!message) return null;

    return {
      text: message,
      progress: deadlineProgress(
        calendar?.registrationStartsAt,
        now,
        edition?.insertedAt || edition?.createdAt,
      ),
    };
  }

  if (state === 'registering') {
    const message = getMessage(
      'Pages:ParticipantWeb.ContestsPage.registrationEndsInDays',
      'Registration ends in {{days}} days',
      calendar?.registrationEndsAt,
    );
    if (!message) return null;

    return {
      text: message,
      progress: deadlineProgress(
        calendar?.registrationEndsAt,
        now,
        calendar?.registrationStartsAt,
      ),
    };
  }

  if (state === 'storing_entries') {
    const message = getMessage(
      'Pages:ParticipantWeb.ContestsPage.receptionEndsInDays',
      'Reception ends in {{days}} days',
      calendar?.storingEndsAt,
    );
    if (!message) return null;

    return {
      text: message,
      progress: deadlineProgress(
        calendar?.storingEndsAt,
        now,
        calendar?.storingStartsAt,
      ),
    };
  }

  if (state === 'in_progress') {
    const targetDate = calendar?.resultsRevealAt || calendar?.contestEndsAt;
    const message = calendar?.resultsRevealAt
      ? getMessage(
          'Pages:ParticipantWeb.ContestsPage.resultsInDays',
          'Results in {{days}} days',
          targetDate,
        )
      : getMessage(
          'Pages:ParticipantWeb.ContestsPage.judgingEndsInDays',
          'Judging ends in {{days}} days',
          targetDate,
        );
    if (!message) return null;

    return {
      text: message,
      progress: deadlineProgress(targetDate, now, calendar?.contestStartsAt),
    };
  }

  return null;
};

const statusPillClasses = (status?: string) => {
  if (status === 'registering') {
    return 'border-emerald-300/45 bg-emerald-500/15 text-emerald-800 dark:border-emerald-500/35 dark:bg-emerald-500/20 dark:text-emerald-200';
  }

  if (status === 'published' || status === 'storing_entries') {
    return 'border-sky-300/45 bg-sky-500/15 text-sky-800 dark:border-sky-500/35 dark:bg-sky-500/20 dark:text-sky-200';
  }

  if (status === 'in_progress') {
    return 'border-amber-300/45 bg-amber-500/15 text-amber-800 dark:border-amber-500/35 dark:bg-amber-500/20 dark:text-amber-200';
  }

  if (status === 'finished' || status === 'results_revealed') {
    return 'border-slate-300/50 bg-slate-400/20 text-slate-700 dark:border-slate-500/40 dark:bg-slate-500/20 dark:text-slate-200';
  }

  return 'border-content-300/45 bg-content-300/20 text-content-700 dark:border-content-700/45 dark:bg-content-700/20 dark:text-content-300';
};

const formatStatusLabel = (status: string | undefined, t: TranslateFn) => {
  if (status === 'registering') {
    return t(
      'Pages:ParticipantWeb.ContestsPage.statusLabels.registering',
      'Rejestracja',
    );
  }
  if (status === 'published') {
    return t(
      'Pages:ParticipantWeb.ContestsPage.statusLabels.published',
      'Nadchodzący',
    );
  }
  if (status === 'storing_entries') {
    return t(
      'Pages:ParticipantWeb.ContestsPage.statusLabels.storing_entries',
      'Recepcja',
    );
  }
  if (status === 'in_progress') {
    return t(
      'Pages:ParticipantWeb.ContestsPage.statusLabels.in_progress',
      'Ocenianie',
    );
  }
  if (status === 'finished') {
    return t(
      'Pages:ParticipantWeb.ContestsPage.statusLabels.finished',
      'Zakończony',
    );
  }
  if (status === 'results_revealed') {
    return t(
      'Pages:ParticipantWeb.ContestsPage.statusLabels.results_revealed',
      'Wyniki',
    );
  }
  if (status === 'cancelled') {
    return t(
      'Pages:ParticipantWeb.ContestsPage.statusLabels.cancelled',
      'Odwołany',
    );
  }
  return t('Pages:ParticipantWeb.ContestsPage.statusLabels.draft', 'Szkic');
};

const getCurrentEdition = (contest?: any) =>
  contest?.currentEdition?.data ||
  contest?.contestsEditions?.data?.find((edition: any) => edition?.current) ||
  contest?.contestsEditions?.data?.[0];

export const ContestInfoCard = ({
  contest,
  onClick,
  applicationState,
  myEntries,
  t: propT,
  now = new Date(),
}: ContestInfoCardProps) => {
  const [tHook] = useTranslation('Pages');
  const t = propT || tHook;

  const currentEdition = getCurrentEdition(contest);
  const state = currentEdition?.state;

  const locationCity = currentEdition?.location?.city;
  const locationCountry = currentEdition?.location?.country;
  const locationLabel =
    locationCity && locationCountry
      ? `${locationCity} / ${locationCountry}`
      : locationCity ||
        locationCountry ||
        t(
          'Pages:ParticipantWeb.ContestsPage.unknownLocation',
          'Unknown location',
        );

  const isFinished = state === 'finished' || state === 'results_revealed';
  const countdownInfo = getCountdownInfo(currentEdition, now, t);

  const entriesCount = safeNumber(currentEdition?.entriesCount);
  const stylesCount =
    currentEdition?.contestsEditionStyles?.data?.length ||
    currentEdition?.contestsEditionStyles?.length ||
    currentEdition?.contests_edition_styles?.data?.length ||
    currentEdition?.contests_edition_styles?.length;
  const categoriesCount = safeNumber(
    stylesCount ||
      currentEdition?.stats?.categoriesCount ||
      currentEdition?.stats?.stylesCount,
  );

  const resultsUrl =
    currentEdition?.settings?.website?.url ||
    currentEdition?.website?.url ||
    contest?.url;

  const statusBadge = (
    <span
      className={classNames(
        'shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold',
        statusPillClasses(state),
      )}
    >
      {formatStatusLabel(state, t)}
    </span>
  );

  return (
    <Card onClick={onClick}>
      <div className="col-span-12 relative flex h-full flex-col">
        <div className="flex items-start justify-between gap-3 md:hidden">
          <h3 className="pb-0.5 text-left font-header text-lg font-semibold leading-[1.3] text-slate-900 dark:text-content-100">
            {contest?.name}
          </h3>
          {statusBadge}
        </div>

        <div className="mt-3 flex h-full gap-6 md:mt-0">
          <div className="hidden md:flex w-20 shrink-0 flex-col items-start gap-2 md:mr-6">
            <div className="h-20 w-20 rounded-2xl border border-white/20 bg-white/40 p-3 dark:border-content-700/45 dark:bg-content-900/45">
              {currentEdition?.logo ? (
                <img
                  src={currentEdition.logo}
                  alt={contest?.name}
                  className="h-full w-full object-contain"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <div className="icon-[ph--trophy] h-9 w-9 text-amber-600/80" />
                </div>
              )}
            </div>
          </div>

          <div className="flex min-w-0 flex-1 flex-col">
            <div className="hidden items-start justify-between gap-3 md:flex">
              <h3
                className="max-h-[3.5rem] overflow-hidden text-left font-header text-lg font-semibold leading-tight text-slate-900 dark:text-content-100"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {contest?.name}
              </h3>
              {statusBadge}
            </div>

            <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-600 dark:text-content-300 md:mt-0">
              <span className="icon-[ph--map-pin] h-4 w-4 text-slate-500 dark:text-content-500" />
              <span>{locationLabel}</span>
            </div>

            <div className="mt-3">
              {countdownInfo && (
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/45 bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-800 dark:border-amber-500/35 dark:bg-amber-500/20 dark:text-amber-200">
                    <span className="icon-[ph--hourglass-medium] h-3.5 w-3.5" />
                    <span>{countdownInfo.text}</span>
                  </div>

                  <div className="hidden h-1.5 w-full rounded-full bg-white/45 md:block">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400"
                      style={{
                        width: `${Math.max(0, Math.min(100, countdownInfo.progress * 100))}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {isFinished && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300/45 bg-slate-300/20 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:border-slate-500/40 dark:bg-slate-500/20 dark:text-slate-200">
                    <span className="icon-[ph--check-circle] h-3.5 w-3.5" />
                    {t(
                      'Pages:ParticipantWeb.ContestsPage.finished',
                      'Finished',
                    )}
                  </span>
                  {resultsUrl && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/45 bg-emerald-500/15 px-2.5 py-1 text-xs font-semibold text-emerald-800 dark:border-emerald-500/35 dark:bg-emerald-500/20 dark:text-emerald-200">
                      <span className="icon-[ph--chart-bar] h-3.5 w-3.5" />
                      {t(
                        'Pages:ParticipantWeb.ContestsPage.resultsAvailable',
                        'Results available',
                      )}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="mt-auto flex flex-wrap gap-1.5 pt-3">
              <span className="inline-flex items-center gap-1 rounded-full border border-white/20 bg-white/35 px-2.5 py-1 text-xs text-slate-700 dark:border-content-700/45 dark:bg-content-900/35 dark:text-content-200">
                <span className="icon-[ph--ticket] h-3.5 w-3.5 text-slate-500 dark:text-content-500" />
                {t(
                  'Pages:ParticipantWeb.ContestsPage.entriesCount',
                  '{{count}} entries',
                  {
                    count: entriesCount,
                  },
                )}
              </span>

              {myEntries !== undefined && (
                <span className="inline-flex items-center gap-1 rounded-full border border-white/20 bg-white/35 px-2.5 py-1 text-xs text-slate-700 dark:border-content-700/45 dark:bg-content-900/35 dark:text-content-200">
                  <span className="icon-[ph--user] h-3.5 w-3.5 text-slate-500 dark:text-content-500" />
                  {t(
                    'Pages:ParticipantWeb.ContestsPage.myEntriesCount',
                    'You: {{count}}',
                    {
                      count: myEntries,
                    },
                  )}
                </span>
              )}

              {applicationState === 'applied' && (
                <span className="inline-flex items-center gap-1 rounded-full border border-sky-300/45 bg-sky-500/15 px-2.5 py-1 text-xs font-semibold text-sky-800 dark:border-sky-500/35 dark:bg-sky-500/20 dark:text-sky-200">
                  <span className="icon-[ph--hand-pointing] h-3.5 w-3.5" />
                  {t('Pages:JudgeWeb.ContestsPage.badges.applied', 'Applied')}
                </span>
              )}

              {applicationState === 'approved' && (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300/45 bg-emerald-500/15 px-2.5 py-1 text-xs font-semibold text-emerald-800 dark:border-emerald-500/35 dark:bg-emerald-500/20 dark:text-emerald-200">
                  <span className="icon-[ph--briefcase] h-3.5 w-3.5" />
                  {t('Pages:JudgeWeb.ContestsPage.badges.assigned', 'Assigned')}
                </span>
              )}

              <span className="inline-flex items-center gap-1 rounded-full border border-white/20 bg-white/35 px-2.5 py-1 text-xs text-slate-700 dark:border-content-700/45 dark:bg-content-900/35 dark:text-content-200">
                <span className="icon-[ph--stack] h-3.5 w-3.5 text-slate-500 dark:text-content-500" />
                {t(
                  'Pages:ParticipantWeb.ContestsPage.categoriesCount',
                  '{{count}} categories',
                  {
                    count: categoriesCount,
                  },
                )}
              </span>
            </div>
          </div>
        </div>

        {countdownInfo && (
          <div className="mt-3 h-1.5 w-full rounded-full bg-white/45 md:hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-amber-500 to-amber-400"
              style={{
                width: `${Math.max(0, Math.min(100, countdownInfo.progress * 100))}%`,
              }}
            />
          </div>
        )}
      </div>
    </Card>
  );
};
